import { observable, action, computed, autorun } from 'mobx'
import { setter }                                from 'mobx-decorators'
import _                                         from 'lodash'

import xhr                                       from 'helpers/XHR'
import getError                                  from 'helpers/ErrorParser'
import uiStore                                   from 'stores/UiStore'
import studentStore                              from 'stores/StudentStore'
import Pagination                                from 'stores/models/Pagination'

export const FILTER = {
  ALL:    'all',
  UNREAD: 'unread'
}

export class SMSInboxStore {
  @setter @observable isLoading = false
  @setter @observable isError = null
  @setter @observable isReadingAllMessages = null
  @setter @observable filter = 'all'
  @setter @observable showContactSelector = false
  @setter @observable totalUnread = null
  @setter @observable totalInbox = null

  @observable inbox = observable.map()
  @observable pagination = new Pagination(this)

  constructor() {
    this.initAutoruns()
  }

  // Computed
  @computed
  get visibleInbox() {
    if (this.filter === FILTER.UNREAD) {
      return this.inbox
        .values()
        .filter(
          (m) => m.direction === 'inbound' && m.total_unread > 0
        )
    }
    return _.orderBy(this.inbox.values(), 'created_at', 'desc')
  }

  @computed get isFetchingInitial() {
    return this.isLoading && !this.pagination.total
  }

  @computed get isUnreadSelected() {
    return this.filter === FILTER.UNREAD
  }

  @computed get paginationParams() {
    return {
      page:  this.pagination.current,
      limit: this.pagination.pageSize
    }
  }

  // Actions
  @action
  initAutoruns = () => {
    this.autoErrorNotifier()
  }

  @action
  autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun('Watch errors', () => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    this.isError.type || 'error'
        })
      }
    })
  }

  @action
  fetchTotalInbox = async() => {
    const { headers } = await xhr.get('/commo/inbox', {
      params: {
        limit: 1,
        only:  'id'
      }
    })

    this.setTotalInbox(headers.total)
  }

  @action
  fetchTotalUnread = async() => {
    const { headers } = await xhr.get('/commo/inbox', {
      params: {
        limit:       1,
        only:        'id',
        only_unread: true
      }
    })

    this.setTotalUnread(parseInt(headers.total))
  }

  @action
  fetchInbox = async() => {
    try {
      this.setIsLoading(true)

      const res = await xhr.get('/commo/inbox', {
        params: {
          ...this.paginationParams,
          only_unread: this.isUnreadSelected,
          only:        [
            'id',
            'conversation_id',
            'body',
            'media_url',
            'media_type',
            'created_at',
            'direction',
            'read_status',
            'total_unread',
            'explicit_content',
            'broker.id',
            'broker.active',
            'broker.contact.name',
            'broker.contact.phone',
            'broker.contact.id',
            'broker.contact.student.full_name',
            'broker.contact.student_id',
            'broker.contact.relationship',
            'broker.contact.flags_count',
            'broker.contact.preferred_language',
            'mute'
          ].join(',')
        }
      })

      this.fetchInboxOK(res)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsLoading(false)
    }
  }

  @action.bound
  fetchInboxOK({ headers, data }) {
    this.isLoading = false
    this.setPagination(headers)

    data.forEach((i) => {
      this.inbox.set(i.conversation_id, i)
    })
  }

  @action setPagination = ({ total }) => {
    this.pagination.setTotal(parseInt(total))
    this.pagination.calculateTotalResults()
  }

  @action onPageChange = () => {
    this.fetchInbox()
  }

  @action addInboxItem = (msg) => {
    const { direction, conversation_id: conversationID } = msg

    if (direction === 'outbound') {
      this.inbox.set(conversationID, msg)
      return
    }

    const conversation = this.inbox.get(conversationID)

    if (conversation) {
      const { total_unread: totalUnread } = conversation

      // Only increment global count if this conversation was previously read
      if (totalUnread === 0) {
        this.setTotalUnread((this.totalUnread || 0) + 1)
      }

      // Update conversation's unread count
      this.inbox.set(conversationID, {
        ...msg,
        total_unread: totalUnread + 1
      })
    } else {
      // New conversation - set total_unread to 1 for this first inbound message
      this.inbox.set(conversationID, { ...msg, total_unread: 1 })
      
      // Increment global unread count for new conversation
      this.setTotalUnread((this.totalUnread || 0) + 1)
    }
  }

  @action handleFilter = (selection) => {
    this.setFilter(selection)
    this.pagination.clear()
    this.fetchInbox()
  }

  @action filteredInboxLength = () => {
    if (this.isUnreadSelected)
      return this.inbox.values().filter((m) => m.total_unread).length
    return this.inbox.size
  }

  @action onContactSelectorClose = () => {
    this.setShowContactSelector(false)
    studentStore.setCurrentStudent(null)
    studentStore.searchedStudents.clear()
  }

  @action updateContactLanguage = (newContact) => {
    if (!this.inbox.size) return

    const { contact } = _.find(
      this.inbox.values(),
      (i) => i.broker.contact.id === newContact.id
    ).broker

    contact.preferred_language = newContact.preferred_language
  }

  @action readAll = async() => {
    try {
      this.setIsReadingAllMessages(true)

      const res = await xhr.post('/commo/sms/conversations/read')

      this.readAllOk(res)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsReadingAllMessages(false)
    }
  }

  @action setAllAsRead = () => {
    this.inbox.forEach((item, key) => {
      this.inbox.set(key, { ...item, total_unread: 0 })
    })
  }

  @action readAllOk = ({ data }) => {
    uiStore.addMessage(
      `${data} ${
        data > 1 ? 'conversations' : 'conversation'
      } marked as read!`,
      'success'
    )
    this.setTotalUnread(0)
    this.setAllAsRead()
  }
}

export default new SMSInboxStore()
